import * as React from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Link, router, Stack, useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { isEmpty, omit } from "lodash";
import { Controller } from "react-hook-form";
import { useIsFetching } from "react-query";

import { useAccountActions } from "~/components/AccountActions/context";
import { Image } from "~/components/Image";
import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { SaveFormBreadcrumbs } from "~/components/SaveForm/Breadcrumbs";
import { useSaveFormContext } from "~/components/SaveForm/shared";
import {
  blurhash,
  icons,
  isWeb,
  isWebDesktop,
  useHackyParams,
} from "~/lib/constants";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { TextInput } from "~/ui/TextInput";
import { FilePreviewCard } from "./FilePreviewCard";
import { MediaPreviewCard } from "./MediaPreviewCard";

export default function SaveFormForm() {
  const {
    store: { slug, spaceById, spaceBySlug, createSave, updateSave },
  } = useAccountStore();

  const { push } = useRouter();

  const { control, setValue, handleSubmit, getValues, formState } =
    useSaveFormContext();
  const isUpdating = getValues("intent") === "update";

  const safeInsets = useSafeAreaInsets();

  const saveType = getValues("type");

  const { removeModalByType } = useAccountActions();

  const params = useHackyParams<{ space: string; filters?: string }>();

  const isUploading =
    useIsFetching({
      queryKey: ["media_upload"],
    }) !== 0;

  const isScraping =
    useIsFetching({
      queryKey: ["scrape"],
    }) !== 0;
  return (
    <>
      <ScrollView>
        <View
          className="container gap-y-4"
          style={{
            paddingBottom: safeInsets.bottom,
          }}
        >
          <ResponsiveRenderer
            webDesktop={<SaveFormBreadcrumbs />}
            default={
              <Stack.Screen
                options={{
                  headerTitle: `Select a Workspace`,
                }}
              />
            }
          />

          {(() => {
            switch (saveType) {
              case "URL": {
                return (
                  <Controller
                    control={control}
                    name="urlMeta"
                    render={({ field }) => {
                      return (
                        <View className="flex-row gap-4">
                          <View className="grow" style={{ height: 100 }}>
                            <Image
                              source={field.value?.image ?? field.value?.logo}
                              placeholder={blurhash}
                              style={{
                                flex: 1,
                                // height: 80,
                                height: 100,
                                borderRadius: 8,
                              }}
                              transition={1000}
                              contentFit="cover"
                              alt={field.value?.title}
                            />
                          </View>
                          <View className="flex-1 items-start gap-2">
                            <View className="flex-row items-center gap-1">
                              {field.value?.logo && (
                                <Image
                                  source={field.value.logo}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 4,
                                  }}
                                  contentFit="cover"
                                  alt={field.value.title}
                                />
                              )}
                              {field.value?.title && (
                                <Text
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                  className="text-text-primary shrink font-semibold"
                                >
                                  {field.value.title}
                                </Text>
                              )}
                            </View>
                            {field.value?.description && (
                              <Text
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                className="text-text-secondary shrink text-sm"
                              >
                                {field.value.description}
                              </Text>
                            )}
                            {isWebDesktop() ? (
                              <Button
                                onPress={() => {
                                  setValue("step", "addUrl");
                                }}
                                variant="secondary"
                                size="small"
                                className="mt-auto h-auto p-0"
                              >
                                Edit Link
                              </Button>
                            ) : (
                              <Link
                                href={{
                                  pathname:
                                    "/(app)/(authenticated)/[account]/link/editLink",
                                  params: {
                                    account: slug,
                                  },
                                }}
                                asChild
                              >
                                <Text className="text-text-brand">
                                  Edit Link
                                </Text>
                              </Link>
                            )}
                          </View>
                        </View>
                      );
                    }}
                  />
                );
              }
              case "MEDIA": {
                return <MediaPreviewCard />;
              }

              case "FILE": {
                return <FilePreviewCard />;
              }

              default: {
                return null;
              }
            }
          })()}

          <Controller
            control={control}
            name="title"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label="Title"
                onChangeText={field.onChange}
                value={field.value ?? ""}
                autoFocus
                placeholder=""
                errorMessage={fieldState.error?.message}
                // onBlur={field.onBlur}
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label="Description"
                onChangeText={field.onChange}
                value={field.value ?? ""}
                multiline
                numberOfLines={4}
                placeholder=""
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="space"
            render={({ field: spaceField }) => {
              if (!spaceField.value) return <></>;

              const space = spaceById(spaceField.value.id);
              if (!space) return <></>;

              return (
                <Pressable
                  onPress={() => {
                    if (isWebDesktop()) {
                      return setValue("step", "selectLocation");
                    }

                    push({
                      pathname:
                        "/(app)/(authenticated)/[account]/link/selectLocation",
                      params: {
                        account: slug,
                      },
                    });
                  }}
                  className="gap-2"
                >
                  <View className="flex-row items-center gap-1">
                    <Text className="text-input-label text-base font-semibold">
                      Location
                    </Text>
                  </View>
                  <Pressable
                    role="button"
                    // TODO: why does this not bubble up?
                    onPress={() => {
                      if (isWebDesktop()) {
                        return setValue("step", "selectLocation");
                      }
                      push({
                        pathname:
                          "/(app)/(authenticated)/[account]/link/selectLocation",
                        params: {
                          account: slug,
                        },
                      });
                    }}
                    className="border-input-border focus:bg-input-background-active focus:border-input-border-active text-text-primary min-h-10 flex-row items-center gap-2 rounded-lg border p-4"
                  >
                    <>
                      {(() => {
                        return (
                          <View className="shrink grow gap-4">
                            <View className="flex-row gap-1">
                              <Feather
                                size={16}
                                name={icons.workspace.web}
                                className="text-text-primary"
                              />
                              <Text className="text-text-primary">
                                {space?.slug ?? space?.displayName}{" "}
                              </Text>
                            </View>
                            {(() => {
                              if (!spaceField.value.collections) return null;
                              if (isEmpty(spaceField.value.collections))
                                return null;

                              return (
                                <View className="flex-row gap-1">
                                  <Feather
                                    size={16}
                                    name={icons.collection.web}
                                    className="text-text-primary"
                                  />

                                  {spaceField.value.collections
                                    .filter(Boolean)
                                    .map((collectionId, i, arr) => (
                                      <Text
                                        className="text-text-primary"
                                        key={collectionId}
                                      >
                                        {space?.collectionById(collectionId)
                                          ?.title ??
                                          space?.collectionById(collectionId)
                                            ?.slug}
                                        {i !== arr.length - 1 ? "," : ""}
                                      </Text>
                                    ))}
                                </View>
                              );
                            })()}

                            {(() => {
                              if (!spaceField.value.tags) return null;
                              if (isEmpty(spaceField.value.tags)) return null;

                              return (
                                <View className="min-w-0 grow flex-row flex-wrap gap-1">
                                  <Feather
                                    size={16}
                                    name={icons.tag.web}
                                    className="text-text-primary"
                                  />

                                  {spaceField.value.tags
                                    .filter(Boolean)
                                    .map((tagIdOrValue, i, arr) => (
                                      <Text
                                        className="text-text-primary"
                                        key={tagIdOrValue}
                                      >
                                        {space?.tagById(tagIdOrValue)?.value}
                                        {i !== arr.length - 1 ? "," : ""}
                                      </Text>
                                    ))}
                                </View>
                              );
                            })()}
                          </View>
                        );
                      })()}
                    </>

                    <Feather
                      className="text-text-secondary ml-auto shrink-0"
                      name="chevron-right"
                      size={16}
                    />
                  </Pressable>
                </Pressable>
              );
            }}
          />

          <View />

          <Button
            role="button"
            onPress={handleSubmit((data) => {
              if (isUpdating) {
                console.log({ data });
                const initialCollections =
                  spaceById(formState.defaultValues?.space?.id ?? "")
                    ?.collectionsAsArray.filter((collection) =>
                      formState.defaultValues?.space?.collections?.includes(
                        collection.id,
                      ),
                    )
                    .map((c) => c.id) ?? [];

                const newCollections =
                  spaceById(data.space.id)
                    ?.collectionsAsArray.filter((collection) =>
                      data.space.collections?.includes(collection.id),
                    )
                    .map((collection) => collection.id) ?? [];

                const addToCollections: Array<string> = [];
                const removeFromCollections: Array<string> = [];

                newCollections.forEach((collectionId) => {
                  if (!initialCollections.includes(collectionId)) {
                    addToCollections.push(collectionId);
                  }
                });

                initialCollections.forEach((collectionId) => {
                  if (!newCollections.includes(collectionId)) {
                    removeFromCollections.push(collectionId);
                  }
                });

                const initialTags =
                  spaceById(formState.defaultValues?.space?.id ?? "")
                    ?.tagsAsArray.filter((tag) =>
                      formState.defaultValues?.space?.tags?.includes(tag.id),
                    )
                    .map((c) => c.id) ?? [];

                const newTags =
                  spaceById(data.space.id)
                    ?.tagsAsArray.filter((tag) =>
                      data.space.tags?.includes(tag.id),
                    )
                    .map((tag) => tag.id) ?? [];

                const tagsToAdd: Array<string> = [];
                const tagsToRemove: Array<string> = [];

                newTags.forEach((tagId) => {
                  if (!initialTags.includes(tagId)) {
                    tagsToAdd.push(tagId);
                  }
                });

                initialTags.forEach((tagId) => {
                  if (!newTags.includes(tagId)) {
                    tagsToRemove.push(tagId);
                  }
                });

                const currentSpace = spaceBySlug(params.space ?? "");

                updateSave({
                  saveId: data.id!,
                  spaceId: currentSpace!.id,
                  set: {
                    ...data,
                    spaceId: data.space.id,
                    addToCollections,
                    removeFromCollections,
                    tagsToAdd,
                    tagsToRemove,
                  },
                });

                removeModalByType("editSave");
              } else {
                const collections =
                  spaceById(data.space.id)
                    ?.collectionsAsArray.filter((collection) =>
                      data.space.collections?.includes(collection.id),
                    )
                    .map((collection) => collection.id) ?? [];

                const tags =
                  spaceById(data.space.id)
                    ?.tagsAsArray.filter((tag) =>
                      data.space.tags?.includes(tag.id),
                    )
                    .map((tag) => tag.id) ?? [];

                switch (data.type) {
                  case "URL": {
                    createSave({
                      spaceId: data.space.id,
                      set: {
                        ...omit(data, "urlMeta"),
                        collections,
                        tags,
                        meta: data.urlMeta,
                      },
                    });
                    break;
                  }
                  case "MEDIA": {
                    createSave({
                      spaceId: data.space.id,
                      set: {
                        ...omit(data, "mediaMeta"),
                        collections,
                        tags,
                        meta: omit(data.mediaMeta, "uri"),
                      },
                    });
                    break;
                  }
                  case "FILE": {
                    createSave({
                      spaceId: data.space.id,
                      set: {
                        ...omit(data, "fileMeta"),
                        collections,
                        tags,
                        meta: omit(data.fileMeta, ["uri", "file"]),
                      },
                    });
                    break;
                  }
                  case "NOTE": {
                    createSave({
                      spaceId: data.space.id,
                      set: {
                        ...data,
                        collections,
                        tags,
                      },
                    });
                    break;
                  }
                }

                removeModalByType("addSave");
              }

              if (isUpdating) {
                if (isWebDesktop()) {
                  return push({
                    pathname:
                      "/(app)/(authenticated)/[account]/(space)/[space]/saves",
                    params: {
                      ...params,
                      account: slug,
                      space: spaceById(data.space.id)?.slug ?? "",
                    },
                  });
                }
                // we should _always_ be able to go back, but just in case
                return router.canGoBack() && router.back();
              }

              // TODO: Where do we go after saving? Do we choose 1st space and take user there?
              push({
                pathname:
                  "/(app)/(authenticated)/[account]/(space)/[space]/saves",
                params: {
                  ...params,
                  account: slug,
                  space: spaceById(data.space.id)?.slug ?? "",
                },
              });
            })}
            disabled={isUploading || isScraping}
          >
            {(() => {
              if (isUploading) {
                return "Uploading asset...";
              }
              if (isScraping) {
                return "Scraping URL...";
              }
              if (isUpdating) {
                return "Update save";
              }
              return "Save";
            })()}
          </Button>
          <Button
            variant="outline"
            onPress={() => {
              if (isWeb) {
                if (isUpdating) {
                  return removeModalByType("editSave");
                }
                return removeModalByType("addSave");
              }
              if (isUpdating && router.canGoBack()) {
                router.back();
              }
            }}
          >
            Cancel
          </Button>

          <View />
        </View>
      </ScrollView>
    </>
  );
}
